import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlanningPeriodProvider } from "@/context/PlanningPeriodContext";
import { UIProvider } from "@/context/UIContext";
import { ProtectedRoute, homePath } from "@/components/ProtectedRoute";
import { AppShell } from "@/components/AppShell";

import Login from "@/pages/Login";
import Checkin from "@/pages/Checkin";
import SetPassword from "@/pages/SetPassword";
import VolunteerDashboard from "@/pages/volunteer/VolunteerDashboard";
import Availability from "@/pages/volunteer/Availability";
import MyShifts from "@/pages/volunteer/MyShifts";
import TeamSchedule from "@/pages/volunteer/TeamSchedule";
import Activities from "@/pages/Activities";
import MyPreferences from "@/pages/volunteer/MyPreferences";
import MyStats from "@/pages/volunteer/MyStats";
import Swaps from "@/pages/Swaps";
import PlannerDashboard from "@/pages/planner/PlannerDashboard";
import PlanningBoard from "@/pages/planner/PlanningBoard";
import ServicesPage from "@/pages/planner/ServicesPage";
import VolunteersPage from "@/pages/planner/VolunteersPage";
import DistributionPage from "@/pages/planner/DistributionPage";
import SwapsManage from "@/pages/planner/SwapsManage";
import ImportExport from "@/pages/planner/ImportExport";
import AttendancePage from "@/pages/planner/AttendancePage";
import AvailabilityStatus from "@/pages/planner/AvailabilityStatus";
import UsersPage from "@/pages/admin/UsersPage";
import AuditLog from "@/pages/admin/AuditLog";
import SettingsPage from "@/pages/admin/SettingsPage";

const P = ["planner", "admin"];

function HomeRedirect() {
  const { user } = useAuth();
  if (user === undefined) return null;
  return <Navigate to={homePath(user)} replace />;
}

function Shell({ children, roles }) {
  return (
    <ProtectedRoute roles={roles}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <AuthProvider>
      <UIProvider>
      <BrowserRouter>
        <PlanningPeriodProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/checkin" element={<Checkin />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/" element={<HomeRedirect />} />

            <Route path="/dashboard" element={<Shell><VolunteerDashboard /></Shell>} />
            <Route path="/availability" element={<Shell><Availability /></Shell>} />
            <Route path="/shifts" element={<Shell><MyShifts /></Shell>} />
            <Route path="/team" element={<Shell><TeamSchedule /></Shell>} />
            <Route path="/activities" element={<Shell><Activities /></Shell>} />
            <Route path="/preferences" element={<Shell><MyPreferences /></Shell>} />
            <Route path="/swaps" element={<Shell><Swaps /></Shell>} />
            <Route path="/stats" element={<Shell><MyStats /></Shell>} />

            <Route path="/planner" element={<Shell roles={P}><PlannerDashboard /></Shell>} />
            <Route path="/board" element={<Shell roles={P}><PlanningBoard /></Shell>} />
            <Route path="/services" element={<Shell roles={P}><ServicesPage /></Shell>} />
            <Route path="/volunteers" element={<Shell roles={P}><VolunteersPage /></Shell>} />
            <Route path="/distribution" element={<Shell roles={P}><DistributionPage /></Shell>} />
            <Route path="/planner-swaps" element={<Shell roles={P}><SwapsManage /></Shell>} />
            <Route path="/import-export" element={<Shell roles={P}><ImportExport /></Shell>} />
            <Route path="/attendance" element={<Shell roles={P}><AttendancePage /></Shell>} />
            <Route path="/availability-status" element={<Shell roles={P}><AvailabilityStatus /></Shell>} />

            <Route path="/admin/users" element={<Shell roles={["admin"]}><UsersPage /></Shell>} />
            <Route path="/admin/audit" element={<Shell roles={["admin"]}><AuditLog /></Shell>} />
            <Route path="/admin/settings" element={<Shell roles={["admin"]}><SettingsPage /></Shell>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </PlanningPeriodProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      </UIProvider>
    </AuthProvider>
  );
}

export default App;
